import React, { useEffect, useState } from 'react';
import {
  Box, CssBaseline, ThemeProvider, createTheme,
  AppBar, Toolbar, Typography, Avatar, Badge, IconButton,
  Tooltip, Divider, Stack, Chip, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { SnackbarProvider } from 'notistack';
import { AppProvider, useApp } from './context/AppContext';
import Sidebar from './components/Sidebar';
import TablePage from './pages/TablePage';
import Dashboard from './pages/Dashboard';
import SalaryTemplatePage from './pages/SalaryTemplatePage';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import { tableLabel } from './config/tableLabels';
import { API_BASE_URL, getAuth } from './services/api';
import LogoutIcon from '@mui/icons-material/Logout';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
    h4: { fontWeight: 800, letterSpacing: -0.5 },
    h5: { fontWeight: 700, letterSpacing: -0.3 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 600, letterSpacing: 0.2 },
  },
  palette: {
    primary: { main: '#22c55e', dark: '#16a34a', light: '#4ade80', contrastText: '#fff' },
    secondary: { main: '#059669', dark: '#047857', contrastText: '#fff' },
    success: { main: '#22c55e', dark: '#16a34a' },
    error: { main: '#EF4444', dark: '#DC2626' },
    warning: { main: '#F59E0B', dark: '#D97706' },
    info: { main: '#22c55e' },
    background: { default: '#F3F4F6', paper: '#FFFFFF' },
    text: { primary: '#0F172A', secondary: '#64748B' },
    divider: '#E2E8F0',
  },
  shape: { borderRadius: 10 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.05)',
    '0 4px 6px -1px rgba(0,0,0,0.08),0 2px 4px -2px rgba(0,0,0,0.05)',
    '0 10px 15px -3px rgba(0,0,0,0.08),0 4px 6px -4px rgba(0,0,0,0.05)',
    '0 20px 25px -5px rgba(0,0,0,0.08),0 8px 10px -6px rgba(0,0,0,0.05)',
    ...Array(19).fill('none'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8, fontWeight: 600, letterSpacing: 0.2 },
        contained: {
          boxShadow: '0 2px 8px rgba(34,197,94,0.25)',
          '&:hover': { boxShadow: '0 4px 16px rgba(34,197,94,0.35)' },
        },
        outlined: { borderWidth: '1.5px', '&:hover': { borderWidth: '1.5px' } },
      },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.05)' } } },
    MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#22c55e' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22c55e', borderWidth: 2 },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 6 } } },
    MuiTableCell: { styleOverrides: { root: { borderColor: '#F1F5F9' } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
    MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 6, fontSize: 12 } } },
  },
});

// Pages that are not table-based (custom routing)
const CUSTOM_PAGES = { SALARY_TEMPLATE: SalaryTemplatePage };

function MainApp({ session, onLogout }) {
  const { state, loadTables, selectTable, clearActiveContext } = useApp();
  const [activeTable, setActiveTable] = useState(null);
  const [activePage, setActivePage] = useState(null);  // e.g. 'SALARY_TEMPLATE'
  const [userMenu, setUserMenu] = useState(null);
  
  // Notification State
  const [notifications, setNotifications] = useState([]);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [clearNotifDialogOpen, setClearNotifDialogOpen] = useState(false);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = async () => {
    try {
      const auth = getAuth();
      const res = await fetch(`${API_BASE_URL}/notifications`, {
        headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' }
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(data.data);
      }
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenNotifications = async (e) => {
    setNotifAnchorEl(e.currentTarget);
    if (unreadCount > 0) {
      try {
        const auth = getAuth();
        await fetch(`${API_BASE_URL}/notifications/mark-read`, {
          method: 'PUT',
          headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' }
        });
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      } catch (e) {
        console.error('Failed to mark read', e);
      }
    }
  };

  const handleCloseNotifications = () => {
    setNotifAnchorEl(null);
  };

  const handleDeleteNotification = async (id, e) => {
    e.stopPropagation();
    try {
      const auth = getAuth();
      const res = await fetch(`${API_BASE_URL}/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' }
      });
      if (res.ok) {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete notification', err);
    }
  };

  const handleClearAllNotificationsClick = (e) => {
    e.stopPropagation();
    setClearNotifDialogOpen(true);
  };

  const handleConfirmClearAllNotifications = async () => {
    setClearNotifDialogOpen(false);
    try {
      const auth = getAuth();
      const res = await fetch(`${API_BASE_URL}/notifications/clear`, {
        method: 'DELETE',
        headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' }
      });
      if (res.ok) {
        setNotifications([]);
      }
    } catch (err) {
      console.error('Failed to clear notifications', err);
    }
  };

  const handleCancelClearAllNotifications = () => {
    setClearNotifDialogOpen(false);
  };

  // Load tables (with safe error handling to avoid runtime overlay when backend is down)
  useEffect(() => { loadTables().catch(() => { }); }, [loadTables]);

  const handleSelect = (name) => {
    if (name.startsWith('__page:')) {
      const pageKey = name.replace('__page:', '');
      setActivePage(pageKey);
      setActiveTable(null);
    } else {
      setActiveTable(name);
      selectTable(name);
      setActivePage(null);
    }
  };

  const activeSchema = activeTable
    ? (state.tables.find(t => t.name === activeTable)?.schema || [])
    : [];

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar tables={state.tables} activeTable={activeTable} activePage={activePage} onSelect={handleSelect}
        activeContext={state.activeContext} onExitContext={clearActiveContext} />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* ── Top AppBar ── */}
        <AppBar position="static" elevation={0}
          sx={{ bgcolor: '#fff', borderBottom: '1px solid #E2E8F0', zIndex: 10 }}>
          <Toolbar variant="dense" sx={{ minHeight: 56, px: 3, gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              {activePage ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">Payroll HRMS</Typography>
                  <Typography color="text.secondary">/</Typography>
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {activePage.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                  </Typography>
                </Stack>
              ) : activeTable ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">Payroll HRMS</Typography>
                  <Typography color="text.secondary">/</Typography>
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {tableLabel(activeTable)}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" fontWeight={700} color="text.primary">Dashboard</Typography>
              )}
            </Box>

            <Tooltip title="Help">
              <IconButton size="small" sx={{ color: '#94A3B8' }}><HelpOutlineIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Notifications">
              <IconButton size="small" sx={{ color: '#94A3B8' }} onClick={handleOpenNotifications}>
                <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}>
                  <NotificationsNoneIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Menu
              anchorEl={notifAnchorEl}
              open={Boolean(notifAnchorEl)}
              onClose={handleCloseNotifications}
              PaperProps={{ sx: { width: 380, maxHeight: 500, mt: 1, borderRadius: 2 } }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700}>Notifications</Typography>
                {notifications.length > 0 && (
                  <Typography 
                    variant="caption" 
                    color="primary" 
                    sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                    onClick={handleClearAllNotificationsClick}
                  >
                    Clear All
                  </Typography>
                )}
              </Box>
              {notifications.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No new notifications</Typography>
                </Box>
              ) : (
                notifications.map((notif) => (
                  <MenuItem key={notif.id} sx={{ whiteSpace: 'normal', py: 1.5, px: 2, borderBottom: '1px solid #F3F4F6', bgcolor: notif.is_read ? 'transparent' : '#F0FDF4', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Typography variant="subtitle2" fontSize={13} fontWeight={600} color="text.primary">
                          {notif.title || 'System Notification'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 2, fontSize: 11 }}>
                          {new Date(Number(notif.created_at) || notif.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" fontSize={13} color="text.secondary" sx={{ mb: 1, lineHeight: 1.4 }}>
                        {notif.message}
                      </Typography>
                      <Chip label={notif.module || notif.type} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#E2E8F0', color: '#475569', fontWeight: 600 }} />
                    </Box>
                    <IconButton size="small" onClick={(e) => handleDeleteNotification(notif.id, e)} sx={{ color: '#94A3B8', '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' } }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </MenuItem>
                ))
              )}
            </Menu>

            <Dialog
              open={clearNotifDialogOpen}
              onClose={handleCancelClearAllNotifications}
              PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 320 } }}
            >
              <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Clear Notifications</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to clear all notifications?
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCancelClearAllNotifications} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
                <Button onClick={handleConfirmClearAllNotifications} color="error" variant="contained" sx={{ fontWeight: 600, borderRadius: 2, boxShadow: 'none' }}>Clear All</Button>
              </DialogActions>
            </Dialog>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#E2E8F0' }} />

            <Stack direction="row" alignItems="center" spacing={1.5}
              onClick={(e) => setUserMenu(e.currentTarget)}
              sx={{
                cursor: 'pointer', px: 0.5, py: 0.5, borderRadius: '8px',
                '&:hover': { bgcolor: '#F9FAFB' }
              }}>
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="caption" fontWeight={700} color="text.primary" display="block" lineHeight={1.2}>
                  {session?.username || 'Admin'}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2}>VVSPL HRMS</Typography>
              </Box>
              <Avatar sx={{
                width: 32, height: 32, fontSize: 13, fontWeight: 700,
                background: 'linear-gradient(135deg,#16a34a,#22c55e)'
              }}>
                {(session?.username || 'A').charAt(0).toUpperCase()}
              </Avatar>
            </Stack>
            <Menu
              anchorEl={userMenu}
              open={!!userMenu}
              onClose={() => setUserMenu(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 0.5, minWidth: 180, borderRadius: '10px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.12)', border: '1.5px solid #E5E7EB'
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #F3F4F6' }}>
                <Typography fontSize={12.5} fontWeight={700} color="#111827">
                  {session?.username || 'Admin'}
                </Typography>
                <Typography fontSize={10.5} color="#9CA3AF">
                  Signed in
                </Typography>
              </Box>
              <MenuItem onClick={() => { setUserMenu(null); onLogout(); }}
                sx={{
                  fontSize: 13, color: '#DC2626', py: 1,
                  '&:hover': { bgcolor: '#FEF2F2' }
                }}>
                <ListItemIcon><LogoutIcon sx={{ fontSize: 16, color: '#DC2626' }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}>
                  Sign Out
                </ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* ── Main Content ── */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#F3F4F6' }}>
          {activePage && CUSTOM_PAGES[activePage] ? (
            React.createElement(CUSTOM_PAGES[activePage])
          ) : activeTable ? (
            <TablePage tableName={activeTable} schema={activeSchema} />
          ) : (
            <Dashboard tables={state.tables} onSelectTable={handleSelect} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function loadStoredSession() {
  try {
    const raw = localStorage.getItem('payroll.auth');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function App() {
  const [session, setSession] = useState(loadStoredSession());
  const [authView, setAuthView] = useState('landing');

  const handleLogout = () => {
    localStorage.removeItem('payroll.auth');
    // Also clear active payroll context on logout
    localStorage.removeItem('payroll.activeContext');
    setSession(null);
    setAuthView('landing');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
      >
        {session ? (
          <AppProvider>
            <MainApp session={session} onLogout={handleLogout} />
          </AppProvider>
        ) : authView === 'login' ? (
          <Login onLogin={setSession} onBackToLanding={() => setAuthView('landing')} />
        ) : (
          <LandingPage onGoToLogin={() => setAuthView('login')} />
        )}
      </SnackbarProvider>
    </ThemeProvider>
  );
}
